<script>
import AiDisease from "./AiDisease.vue";
import ZlifePetDemo from "./ZlifePetDemo.vue";
import SelectableCard from "./SelectableCard.vue";
import { parsePossibleObject } from "./diseaseUtils";
import ZlifepetAnalises from "./ZlifePetAnalises.vue";
import ZlifepetAplicarDiagnostico from "./ZlifePetAplicarDiagnostico.vue";

export default {
  inject: ["hdl"],
  components: {
    ZlifepetAplicarDiagnostico,
    ZlifepetAnalises,
    SelectableCard,
    AiDisease,
    ZlifePetDemo,
  },

  props: {
    anamnese: String,
    examesClinicos: String,
    animalId: {
      type: [String, Number],
    },
  },
  emits: [
    "update:anamnese",
    "update:exames-clinicos",
    "close",
    "aplicar",
  ],

  data() {
    return {
      plano: null,
      animal: null,
      selected: null,
      diagnostics: [],
      generating: false,
      abortController: null,
      showHistorico: false,
      showEscolherAplicacao: false,
    };
  },

  computed: {
    periodoTeste() {
      const { tempo } = this.plano?.demonstracao ?? {};
      if (!tempo) {
        return "Demonstração";
      }
      if (tempo >= 86400) {
        const dias = Math.round(tempo / 86400);
        return `Demonstração - ${dias} ${this.plural("dia", dias)} ${
          this.plural("restante", dias)
        }`;
      }
      if (tempo >= 3600) {
        const horas = Math.round(tempo / 3600);
        return `Demonstração - ${horas} ${this.plural("hora", horas)} ${
          this.plural("restante", horas)
        }`;
      }
      return "Demonstração - Menos de uma hora restante";
    },
    sexo() {
      const sex = this.animal.sexo;
      if (!sex) return "";
      return sex.charAt(0).toUpperCase() + sex.slice(1);
    },
    hasUsos() {
      return this.plano?.status === "ativo" &&
        this.plano.usosRestantes > 0;
    },
    isDemo() {
      return this.plano?.status === "inativo" &&
        (this.plano.demonstracao.tempo ?? 0) > 0;
    },
    podeObterDemo() {
      return this.plano?.status === "inativo" &&
        this.plano?.demonstracao.disponivel &&
        !this.plano?.demonstracao.tempo;
    },
    bloqueado() {
      return this.plano && !this.hasUsos && !this.isDemo && !this.podeObterDemo;
    },
    diagnosticoAtual() {
      return this.diagnostics.find((o) => o.id === this.selected);
    },
    showDiagnostics() {
      return !this.bloqueado || this.diagnostics.length;
    },
    ultimoSolicitante() {
      return String(this.plano?.ultimaSolicitacao?.usuarioId) ===
          String(globalThis.Siggma.usuario)
        ? "Você"
        : this.plano?.ultimaSolicitacao?.usuarioNome;
    },
  },

  methods: {
    plural(str, num) {
      return str + ((num ?? 0) > 1 ? "s" : "");
    },
    solicitar() {
      this.hdl.http({
        url: `${globalThis.Siggma.basePath}/app/zlife-pet-ai/solicitar`,
        callback: true,
      }).then(({ responseText }) => {
        const resp = JSON.parse(responseText);

        if (resp.exception) {
          globalThis.Notify.warning(resp.exception);
          globalThis.Yanda.message({
            type: "warning",
            msg: "Erro ao solicitar",
            detail: resp.exception,
          });
        }
        if (resp.success) {
          const data = new Intl.DateTimeFormat("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          }).format(new Date());

          this.plano.ultimaSolicitacao = {
            data,
            usuarioId: globalThis.Siggma.usuario,
            usuarioNome: globalThis.Siggma.usuarioNome,
          };

          globalThis.Yanda.message({
            type: "success",
            msg: "Solicitação enviada",
            detail: "Solicitação enviada para o Comercial.",
          });
        }
      });
    },
    validarForm() {
      const especie = this.animal.especie;
      if (!especie || typeof especie !== "string") {
        globalThis.Notify.warning(
          "O animal precisa ter sua espécie informada",
        );
        return false;
      }

      const regex = /^(gat.*|cachorr.*|canin.*|felin.*|c[ãa]os?|c[ãa]es)$/i;
      if (!regex.test(especie)) {
        globalThis.Notify.warning(
          "Disponível apenas para felinos e caninos",
        );
        return false;
      }

      const hasAnamnese = this.anamnese && this.anamnese.trim() !== "";
      const hasExamesClinicos = this.examesClinicos &&
        this.examesClinicos.trim() !== "";

      if (!hasAnamnese && !hasExamesClinicos) {
        globalThis.Notify.warning(
          "Preencha pelo menos um campo: Anamnese ou Exame clínico",
        );
        return false;
      }

      return true;
    },
    async getDiagnosticoStream(signal) {
      const { especie, raca, sexo, idade } = this.animal;
      const res = await fetch(
        `${globalThis.Siggma.basePath}/app/zlife-pet-ai/diagnostics`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            especie,
            raca,
            sexo,
            idade,
            anamnese: this.anamnese,
            examesClinicos: this.examesClinicos,
          }),
          signal,
        },
      );

      if (!res.ok) {
        throw new Error("Erro ao analisar");
      }

      const reader = res.body?.getReader();
      if (!reader) {
        throw new Error("Erro de conexão");
      }

      return reader;
    },
    async gerarDiagnosticos() {
      if (this.generating || !this.validarForm()) {
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.selected = null;
      this.diagnostics = [];
      this.generating = true;
      try {
        let buffer = "";
        const decoder = new TextDecoder();
        const reader = await this.getDiagnosticoStream(signal);
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          const lines = buffer.split("\n");

          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.trim()) {
              continue;
            }

            const parsed = parsePossibleObject(line);
            this.diagnostics = [...this.diagnostics, parsed];
          }
        }

        if (buffer.trim()) {
          const parsed = parsePossibleObject(buffer);
          this.diagnostics = [...this.diagnostics, parsed];
        }
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }

        const message = (err instanceof Error ? err.message : "") ||
          "Resposta inválida do servidor";
        globalThis.Yanda.message({
          type: "warning",
          msg: "Erro ao gerar diagnósticos",
          detail: message,
        });
      } finally {
        await this.getPlano();
        this.generating = false;
      }
    },
    aplicar(aplicacao) {
      const diagnostico = this.diagnosticoAtual;
      if (!diagnostico) {
        return globalThis.Notify.warning("Nenhum diagnóstico selecionado");
      }

      this.$emit("aplicar", { diagnostico, aplicacao });
      this.showEscolherAplicacao = false;
      this.$emit("close");
    },
    async getAnimal() {
      if (!this.animalId) {
        return;
      }
      const { nome, especie, idade, raca, sexo } = await this.hdl.http(
        `${globalThis.Siggma.basePath}/pet-shop/animais/get-by-id`,
        { id: this.animalId },
      ).then(({ data }) => data);

      this.animal = { nome, especie, idade, raca, sexo };
    },
    async getPlano() {
      this.plano = await this.hdl.http(
        `${globalThis.Siggma.basePath}/app/zlife-pet-ai/get-plan`,
      ).then(({ data }) => data);
    },
  },

  async mounted() {
    await this.getAnimal();
    await this.getPlano();
  },

  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
};
</script>

<template>
  <s-layout>
    <template v-slot:extra>
      <zlifepet-analises :visible="showHistorico"
        @close="showHistorico = false" />
      <zlifepet-aplicar-diagnostico :visible="showEscolherAplicacao"
        :diagnostico="diagnosticoAtual?.disease?.name" @aplicar="aplicar"
        @close="showEscolherAplicacao = false" />
    </template>

    <form class="s-card dados-animal"
      :style="{flex: showDiagnostics ? 'unset' : 1}">
      <zlife-pet-demo v-if="podeObterDemo"></zlife-pet-demo>
      <s-form-group header="Dados do animal" :collapsable="false">
        <table class="s-petshop-ficha-cadastro-pet-info-table" v-if="animal">
          <tr>
            <th v-if="animal.nome">Nome</th>
            <th v-if="animal.especie">Espécie</th>
            <th v-if="animal.raca">Raça</th>
            <th v-if="sexo">Sexo</th>
            <th v-if="animal.idade">Idade</th>
          </tr>
          <tr>
            <td v-if="animal.nome">{{animal.nome}}</td>
            <td v-if="animal.especie">{{animal.especie}}</td>
            <td v-if="animal.raca">{{animal.raca}}</td>
            <td v-if="sexo">{{sexo}}</td>
            <td v-if="animal.idade">{{animal.idade}}</td>
          </tr>
        </table>
        <div class="s-petshop-ficha-cadastro-row">
          <div>
            <label>Anamnese:</label>
          </div>
          <div>
            <s-textarea-autosize :value="anamnese"
              @input="$emit('update:anamnese', $event)"
              style="width: 100%; height: 50px; resize: vertical;"
              placeholder="Descrição do cliente..."></s-textarea-autosize>
          </div>
        </div>
        <div class="s-petshop-ficha-cadastro-row">
          <div>
            <label>Exame&nbsp;clínico:</label>
          </div>
          <div>
            <s-textarea-autosize :value="examesClinicos"
              @input="$emit('update:exames-clinicos', $event)"
              style="width: 100%; height: 50px; resize: vertical;"
              placeholder="Sintomas identificados pelo veterinário..."></s-textarea-autosize>
          </div>
        </div>
      </s-form-group>
      <div class="aviso-limite" v-if="bloqueado"
        :style="`margin-top: ${diagnostics.length ? 0 : 32}px`">
        <p style="margin-bottom: 2px">
          <template v-if="plano?.status === 'cancelado'">
            Ative a integração novamente para utilizar nosso diagnóstico com
            IA.
          </template>
          <template v-else-if="plano?.status === 'ativo'">
            Você atingiu o limite de
            diagnósticos com IA disponíveis.
          </template>
        </p>
        <p>
          Para solicitar
          {{plano?.status === "ativo" ? "mais diagnósticos" : "acesso à integração"}}
          com o Assistente IA Zlife Pet, você
          pode clicar no botão abaixo para enviar uma solicitação ao nosso
          Comercial.
        </p>
        <button class="btn-info" type="button" @click="solicitar"><i
            class="mdi mdi-chat-processing-outline mdi-18px">Solicitar{{plano?.ultimaSolicitacao ? " novamente" : ""}}</i></button>
        <p v-if="ultimoSolicitante" class="aviso-solicitar-novamente">
          {{ultimoSolicitante}} já solicitou acesso em
          {{plano.ultimaSolicitacao.data}}
        </p>
      </div>
      <template v-else>
        <template v-if="!plano">
          <div class="s-tag s-tag-info-outlined">
            <s-spinner />
          </div>
        </template>
        <div v-else-if="hasUsos" class="s-tag">
          {{plano?.usosRestantes}}
          {{plural('diagnóstico', plano?.usosRestantes)}}
          {{plural('restante', plano?.usosRestantes)}}
        </div>
        <button class="generate-diagnosis-button" type="button"
          :disabled="!plano || !animal" @click="gerarDiagnosticos">
          <template v-if="!generating">
            <i class="mdi mdi-creation mdi-18px">Gerar diagnósticos</i>
          </template>
          <template v-else>
            <s-spinner /> <span>Gerando...</span>
          </template>
        </button>
      </template>
    </form>

    <div class="s-card generated-diagnoses" v-if="showDiagnostics">
      <template v-if="diagnostics.length">
        <selectable-card v-for="diag in diagnostics" :id="diag.id"
          :checked="diag.id === selected" :key="diag.id"
          name="generated-diagnoses" @change="v => selected = v">
          <template v-slot:header><strong
              class="disease-name">{{diag.disease.name}}</strong></template>
          <ai-disease :disease-ai="diag" :key="diag.id"></ai-disease>
        </selectable-card>
      </template>
      <s-spinner v-else-if="generating" />
      <p v-else>Nenhum diagnóstico gerado</p>
    </div>

    <template v-slot:footer>
      <button type="button" @click="showHistorico = true"
        style="margin-right: auto"><i
          class="mdi mdi-clipboard-text-clock-outline mdi-18px">Histórico</i>
      </button>
      <button type="button" @click="$emit('close')">Cancelar</button>
      <button type="button" class="btn-primary" :disabled="!selected">
        <i class="mdi mdi-content-save-outline mdi-18px"
          @click="showEscolherAplicacao = !!selected">
          Aplicar diagnóstico
        </i></button>
    </template>
  </s-layout>
</template>

<style scoped>
.s-spinner {
  --spinner-size: 6px;

  .generate-diagnosis-button>& {
    display: inline-flex;
    --spinner-color: #464646;
  }

  .s-tag>& {
    --spinner-color: currentColor;
  }
}

.generate-diagnosis-button {
  gap: 8px;
  display: flex;
  width: max-content;
  align-items: center;
  margin: 4px auto 0 auto;
  justify-content: center;

  &:disabled {
    cursor: not-allowed;
  }
}

.s-card.dados-animal {
  display: flex;
  margin-bottom: 6px;
  flex-direction: column;
}

.s-tag {
  max-width: unset;
  margin: 8px auto;
  width: min-content;
  display: block;
}

.aviso-limite {
  opacity: 0.9;
  font-size: 0.9em;
  text-align: center;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  flex-direction: column;

  p {
    width: 50ch;
    text-wrap: wrap;
    margin-top: 6px;
    line-height: 1.5;
  }
}

.aviso-solicitar-novamente {
  opacity: 0.8;
  font-size: 0.9em;
  margin-bottom: 0;
}

.disease-name {
  display: block;
  font-size: 14px;
  margin-bottom: 1em;
}

.s-petshop-ficha-cadastro-pet-info-table {
  margin: 12px 0 12px 120px;
  text-align: center;

  th {
    padding: 4px 8px;
  }

  td {
    padding: 0 8px;
  }
}

.generated-diagnoses {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
}

.generated-diagnoses>p {
  margin: auto auto;
  text-align: center;
}

.s-petshop-ficha-cadastro-row {
  display: flex;
  align-items: center;
  margin: 5px;
}

.s-petshop-ficha-cadastro-row>div:first-child,
.s-petshop-ficha-cadastro-row-2>div:first-child {
  min-width: 120px;
  margin-right: 5px;
  text-align: right;
}

.s-petshop-ficha-cadastro-row>div:last-child {
  flex: 1
}
</style>